import { Navigate, type RouteObject } from 'react-router-dom'
import {  RouteErrorScreen } from '@/app/AppErrorBoundary'
import { registerRoutes } from '@/lib/routeTemplate'
import '@/shared/styles/global.css'
import { Dashboard } from '@/features/dashboard/DashboardPage'
import { Modules as Namespaces } from '@/features/modules/ModulesPage'
import { ModuleDetail } from '@/features/modules/ModuleDetailPage'
import { AllTokens } from '@/features/tokens/AllTokensPage'
import { Org } from '@/features/org/OrgPage'
import { PoliciesOverview } from '@/features/policies/PoliciesOverviewPage'
import { GraphPage } from '@/features/graph/GraphPage'
import { StatePage } from '@/features/state/StatePage'
import { PlansPage } from '@/features/plans/PlansPage'
import { Integrations } from '@/features/integrations/IntegrationsPage'
import { Providers } from '@/features/providers/ProvidersPage'
import { OrgLayout } from '@/app/OrgLayout'
import { AuthGuard } from '@/app/AuthGuard'
import { Callback } from '@/app/Callback'
import { SignedOut } from '@/app/SignedOut'

export const routes: RouteObject[] = [
  {
    path: '/callback',
    element: <Callback />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: '/signed-out',
    element: <SignedOut />,
    errorElement: <RouteErrorScreen />,
  },
  {
    element: <AuthGuard />,
    // Data routers catch in-route render throws themselves (default: React
    // Router's unstyled error page) - they never reach the AppErrorBoundary
    // class, so both top-level routes need the branded errorElement.
    errorElement: <RouteErrorScreen />,
    children: [
      // Org-agnostic routes
      { index: true, element: <Dashboard /> },
      { path: 'integrations/:guide?', element: <Integrations /> },
      // Platform operator console. Lazy, so customers never download it; the worker is the
      // authority on who gets in (see admin/AdminGuard.tsx).
      {
        path: 'admin/*',
        lazy: () => import('@/admin/routes').then((m) => ({ Component: m.AdminRoot })),
      },
      // Org-scoped routes - the slug is the first segment and OrgLayout owns
      // validation + URL→context sync for everything beneath it.
      {
        path: 'org/:orgSlug',
        element: <OrgLayout />,
        children: [
          { index: true, element: <Navigate to="modules" replace /> },
          { path: 'modules', element: <Namespaces /> },
          { path: 'modules/:name/:provider/:version?', element: <ModuleDetail /> },
          { path: 'state', element: <StatePage /> },
          { path: 'state/:workspace/:versionId?', element: <StatePage /> },
          { path: 'plans', element: <PlansPage /> },
          { path: 'plans/:workspace/:versionId?', element: <PlansPage /> },
          { path: 'policies', element: <PoliciesOverview /> },
          { path: 'graph', element: <GraphPage /> },
          { path: 'tokens', element: <AllTokens /> },
          // Providers: one unified artefact list (serve upstream + patched versions per org).
          // Auto-discovered vs added-by-hand is an in-page filter, not a route. The artefact
          // drill-down stays in the URL so detail links are deep-linkable/shareable.
          { path: 'providers', element: <Providers /> },
          { path: 'providers/:upstream/:namespace/:type', element: <Providers /> },
          { path: 'settings', element: <Org /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]

// The page-view tracker turns a pathname into the route TEMPLATE it matched (`app.route`, §5) by
// asking react-router to match THIS array - the one the router itself runs on. That is deliberate:
// a separate list of patterns for telemetry would drift the moment a route is added here, and drift
// silently. Registered at module scope, so it is in place before the first render and therefore
// before the first page view.
registerRoutes(routes)
